import { Fragment, useCallback, useRef } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import styled from 'styled-components'
import { Colors, Spacing, Typography } from '../../styles'
import { OVERLAY_COLLISION_PADDING, OVERLAY_MAX_WIDTH } from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { emptyFunction, stopKeydownPropogation } from '../../utils/utils'
import { Icon } from '../atoms/Icon'
import { Divider } from '../atoms/SectionDivider'
import { BodySmall } from '../atoms/typography/Typography'
import {
    FixedSizeIcon,
    GTMenuItem,
    MarginLeftIcon,
    MenuContentShared,
    MenuItemIconColumn,
    MenuItemLabel,
    MenuItemShared,
} from './RadixUIConstants'
import Tip from './Tip'

const DROPDOWN_MENU_ITEM_MAX_WIDTH = '240px'
const DROPDOWN_MENU_ITEM_MAX_HEIGHT = '75vh'
const DROPDOWN_MENU_MODAL_Z_INDEX = 10000
const DropdownMenuContent = styled(DropdownMenu.Content)<{
    $menuInModal?: boolean
    $width?: number
    $textColor?: string
    $fontStyle?: 'large' | 'medium' | 'small'
}>`
    ${MenuContentShared};
    max-height: ${DROPDOWN_MENU_ITEM_MAX_HEIGHT};
    overflow: auto;
    /* Dialog's modal layer disables pointer events on body. This menu is portalled back under body,
       so it must opt in again or it can render above the dialog without accepting a click. */
    ${({ $menuInModal }) => $menuInModal && `z-index: 1000; pointer-events: auto;`}
    /* A trigger-matched width can exceed the screen once the shell is single-pane, so clamp both
       the width and the cap to the viewport. */
    ${({ $width }) => $width && `width: min(${$width}px, ${OVERLAY_MAX_WIDTH});`}
    max-width: ${({ $width }) => `min(${$width ? `${$width}px` : DROPDOWN_MENU_ITEM_MAX_WIDTH}, ${OVERLAY_MAX_WIDTH})`};
    ${({ $textColor }) => $textColor && `color: ${$textColor};`}
    ${({ $fontStyle }) => $fontStyle && Typography.body[$fontStyle]};
    box-sizing: border-box;
    z-index: ${DROPDOWN_MENU_MODAL_Z_INDEX}; // higher than toast z-index of 9999
`
const DropdownMenuItem = styled(DropdownMenu.Item)`
    ${MenuItemShared};
    width: 100%;
    box-sizing: border-box;
`
const LeftMarginAutoContainer = styled.span`
    margin-left: auto;
`
const DropdownMenuSubTrigger = styled(DropdownMenu.SubTrigger)`
    ${MenuItemShared};
`
const DropdownMenuSubContent = styled(DropdownMenu.SubContent)`
    ${MenuContentShared};
    user-select: none;
    max-height: ${DROPDOWN_MENU_ITEM_MAX_HEIGHT};
    overflow: auto;
`
const Description = styled(BodySmall)`
    padding: ${Spacing._8} ${Spacing._12};
    display: block;
`

const getItemKey = (item: GTMenuItem) => `${item.label}${item.icon}${item.iconColor}${item.iconColorHex}`

interface GTDropdownMenuProps {
    items: GTMenuItem[] | GTMenuItem[][] // allow for divided groups of items
    trigger: React.ReactNode // component that opens the dropdown menu when clicked
    align?: 'start' | 'center' | 'end'
    side?: 'top' | 'bottom' | 'left' | 'right'
    isOpen?: boolean
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
    disabled?: boolean
    hideCheckmark?: boolean
    menuInModal?: boolean
    useTriggerWidth?: boolean
    keepOpenOnSelect?: boolean
    fontStyle?: 'large' | 'medium' | 'small'
    description?: string
    itemLayout?: 'checkmark' | 'singleIcon'
}

const GTDropdownMenu = ({
    items,
    trigger,
    align = 'start',
    side = 'bottom',
    isOpen,
    setIsOpen,
    disabled,
    hideCheckmark = false,
    menuInModal = false,
    useTriggerWidth = false,
    keepOpenOnSelect = false,
    fontStyle = 'large',
    description,
    itemLayout = 'checkmark',
}: GTDropdownMenuProps) => {
    const groups = (items.length > 0 && Array.isArray(items[0]) ? items : [items]) as GTMenuItem[][]
    const useSingleIconLayout = itemLayout === 'singleIcon'

    const ConditionalTooltip = useCallback(
        ({ children, tip }: { children: React.ReactNode; tip?: string }) =>
            tip ? (
                <Tip content={tip} side="right">
                    {children}
                </Tip>
            ) : (
                <>{children}</>
            ),
        []
    )

    const renderItemContent = (item: GTMenuItem) => {
        if (item.renderer) return item.renderer()

        if (useSingleIconLayout) {
            return (
                <>
                    <MenuItemIconColumn>
                        {item.icon && <Icon icon={item.icon} color={item.iconColor} colorHex={item.iconColorHex} />}
                    </MenuItemIconColumn>
                    <MenuItemLabel>{item.label}</MenuItemLabel>
                    {item.count && <LeftMarginAutoContainer>({item.count})</LeftMarginAutoContainer>}
                </>
            )
        }

        return (
            <>
                {!hideCheckmark && !item.hideCheckmark && (
                    <FixedSizeIcon visible={item.selected}>
                        <Icon icon={icons.check} />
                    </FixedSizeIcon>
                )}
                {item.icon && <Icon icon={item.icon} color={item.iconColor} colorHex={item.iconColorHex} />}
                <MenuItemLabel>{item.label}</MenuItemLabel>
                {item.count && <LeftMarginAutoContainer>({item.count})</LeftMarginAutoContainer>}
            </>
        )
    }

    const triggerRef = useRef<HTMLButtonElement>(null)
    return (
        <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen} modal={false}>
                <DropdownMenu.Trigger ref={triggerRef} disabled={disabled} asChild>
                    {trigger}
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenuContent
                        onKeyDown={(e) => stopKeydownPropogation(e, ['Escape'], true)}
                        align={align}
                        style={menuInModal ? { zIndex: DROPDOWN_MENU_MODAL_Z_INDEX } : undefined}
                        $menuInModal={menuInModal}
                        $width={useTriggerWidth ? triggerRef.current?.getBoundingClientRect().width : undefined}
                        $fontStyle={fontStyle}
                        side={side}
                        collisionPadding={OVERLAY_COLLISION_PADDING}
                    >
                        {groups.map((group, groupIndex) => (
                            <Fragment key={groupIndex}>
                                <DropdownMenu.Group>
                                    {group.map((item) => (
                                        <Fragment key={getItemKey(item)}>
                                            {item.subItems ? (
                                                <DropdownMenu.Sub>
                                                    <DropdownMenuSubTrigger
                                                        key={getItemKey(item)}
                                                        onClick={item.onClick}
                                                        $textColor={item.textColor}
                                                    >
                                                        {item.icon && (
                                                            <Icon
                                                                icon={item.icon}
                                                                color={item.iconColor}
                                                                colorHex={item.iconColorHex}
                                                            />
                                                        )}
                                                        <MenuItemLabel>{item.label}</MenuItemLabel>
                                                        <MarginLeftIcon>
                                                            <Icon icon={icons.caret_right} />
                                                        </MarginLeftIcon>
                                                    </DropdownMenuSubTrigger>
                                                    <DropdownMenu.Portal>
                                                        <DropdownMenuSubContent>
                                                            {item.subItems.map((subItem) => (
                                                                <ConditionalTooltip
                                                                    key={getItemKey(subItem)}
                                                                    tip={subItem.tip}
                                                                >
                                                                    <DropdownMenuItem
                                                                        textValue={subItem.label}
                                                                        disabled={subItem.disabled}
                                                                        onClick={
                                                                            subItem.disabled
                                                                                ? emptyFunction
                                                                                : subItem.onClick
                                                                        }
                                                                        $disabled={subItem.disabled}
                                                                        $selected={
                                                                            useSingleIconLayout && subItem.selected
                                                                        }
                                                                        $textColor={subItem.textColor}
                                                                        onSelect={
                                                                            subItem.keepOpenOnSelect ||
                                                                            keepOpenOnSelect ||
                                                                            subItem.disabled
                                                                                ? (e) => e.preventDefault()
                                                                                : emptyFunction
                                                                        }
                                                                    >
                                                                        {renderItemContent(subItem)}
                                                                    </DropdownMenuItem>
                                                                </ConditionalTooltip>
                                                            ))}
                                                        </DropdownMenuSubContent>
                                                    </DropdownMenu.Portal>
                                                </DropdownMenu.Sub>
                                            ) : (
                                                <ConditionalTooltip key={getItemKey(item)} tip={item.tip}>
                                                    <DropdownMenuItem
                                                        textValue={item.label}
                                                        // Radix's own prop, so a disabled item is skipped by the
                                                        // keyboard and announced as disabled, not just greyed out.
                                                        disabled={item.disabled}
                                                        onClick={item.disabled ? emptyFunction : item.onClick}
                                                        $disabled={item.disabled}
                                                        $selected={useSingleIconLayout && item.selected}
                                                        $textColor={item.textColor}
                                                        onSelect={
                                                            item.keepOpenOnSelect || keepOpenOnSelect || item.disabled
                                                                ? (e) => e.preventDefault()
                                                                : emptyFunction
                                                        }
                                                    >
                                                        {renderItemContent(item)}
                                                    </DropdownMenuItem>
                                                </ConditionalTooltip>
                                            )}
                                        </Fragment>
                                    ))}
                                    {groupIndex !== groups.length - 1 && <Divider color={Colors.background.sub} />}
                                </DropdownMenu.Group>
                            </Fragment>
                        ))}
                        {description && (
                            <>
                                <Divider color={Colors.background.sub} />
                                <Description color="light">{description}</Description>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    )
}

export default GTDropdownMenu
